import { FC, ReactElement } from 'react';

import {
  Card,
  Divider,
  IconButton,
  Modal,
  ModalProps,
  styled,
  Typography,
} from '@mui/material';
import clsx from 'clsx';

import { IcClose } from '@/assets/images';

export interface BaseModalProps extends Omit<ModalProps, 'children'> {
  open: boolean;
  hideCloseButton?: boolean;
  title?: string;
  classes?: {
    root?: string;
    content?: string;
    title?: string;
    divider?: string;
  };
  children?: ReactElement;
  onClose: () => void;
  customHeader?: ReactElement;
}

const Root = styled(Modal)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  overflow: 'scroll',

  '& .modal-content': {
    minWidth: 600,
    padding: 20,
    borderRadius: 8,
    [theme.breakpoints.up('lg')]: {
      maxWidth: 1000,
    },
    [theme.breakpoints.up('xl')]: {
      maxWidth: 1400,
    },
    '& .header': {
      display: 'flex',
      justifyContent: 'space-between',
    },
    '& .btn-close': {
      color: theme.palette.error.main,
    },
    '& .divider': {
      marginTop: 20,
      marginBottom: 20,
    },
    '& .no-divider': {
      marginTop: 0,
      marginBottom: 0,
    },
    '& .title': {
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
    },
  },

  '& .fixed-width': {
    maxWidth: '600px',
  },
  '& .full-width': {
    width: '100% !important',
  },
}));

export const AppModal: FC<BaseModalProps> = ({
  title,
  classes,
  hideCloseButton,
  onClose,
  children,
  customHeader,
  ...rest
}) => {
  return (
    <Root onClose={onClose} className={classes?.root} {...rest}>
      <Card
        sx={{
          boxShadow: 'none',
          border: 'none',
          outline: 'none',
        }}
        className={clsx('modal-content', classes?.content)}
      >
        {customHeader ? (
          customHeader
        ) : (
          <Typography
            variant="h6"
            component="h2"
            className={clsx('header', classes?.content)}
          >
            {title && (
              <span
                className="title"
                dangerouslySetInnerHTML={{ __html: title }}
              />
            )}
            {!hideCloseButton && (
              <IconButton
                aria-label="close"
                onClick={onClose}
                className="btn-close"
              >
                <IcClose />
              </IconButton>
            )}
          </Typography>
        )}

        {title && <Divider className={clsx('divider', classes?.divider)} />}
        {children}
      </Card>
    </Root>
  );
};
